import React, { useEffect, useRef, useState } from "react";
import "./DailyReservationsTable.scss";
import FullCalendar from "@fullcalendar/react"; // must go before plugins
import resourceTimelinePlugin from "@fullcalendar/resource-timeline"; // a plugin!
import interactionPlugin from "@fullcalendar/interaction"; // needed for dayClick
import momentPlugin from "@fullcalendar/moment-timezone"; // needed for dayClick
import moment from "moment-timezone";
import SweetAlert from "react-bootstrap-sweetalert/lib/dist/SweetAlert";
import { DateTime } from "luxon";

const licenseKey = "0703169169-fcs-1713021308";

/**
 * Pixel floor for each 15-minute slot on the appointments timeline.
 *
 * Left to itself FullCalendar uses `Math.max(30, slotCushionMaxWidth / slotsPerLabel)`,
 * and with 15-minute slots under hourly labels the second term is far below the floor —
 * so the real default is 30px. 60 is that doubled, which is what makes a 15- or
 * 30-minute appointment wide enough to read its title.
 *
 * It is a *minimum*: the table still stretches to fill a wider container, so this only
 * bites on a day long enough to overflow, where it trades an unreadable squeeze for
 * horizontal scrolling. Appointments only — restaurant bookings run an hour or two and
 * are already wide enough, and §10's rule is that restaurant accounts see no change.
 */
const APPOINTMENT_SLOT_MIN_WIDTH = 60;

function DailyReservationsTimeline({ scrollTime, setScrollTime, ...props }) {
  const { reservations, filter, handleEdit, date, resourcesWithReservations, isAppointments, resourceLabel } = props;
  const overbookedIds = props.overbookedIds;
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineResources, setTimelineResources] = useState([]);
  const timelineRef = useRef(null);
  const [showNoResourcesModal, setShowNoResourcesModal] = useState(false);

  moment.tz.setDefault(props.timezone);

  useEffect(() => {
    setScrollTime(DateTime.now().setZone(props.timezone));
  }, []);

  const handleTimelineEventClick = async eventInfo => {
    if (props.disabled) return;
    const reservation = reservations.find(r => r.id === eventInfo.event.id);
    await handleEdit(reservation);
    setScrollTime(DateTime.fromJSDate(eventInfo.date).setZone(props.timezone));
  };

  const handleTimelineDateClick = async eventInfo => {
    if (props.disabled) return;
    let resourceId = eventInfo.resource.id;
    const seperatorIndex = resourceId.indexOf("_");
    if (seperatorIndex > -1 && resourceId) {
      resourceId = resourceId.slice(0, seperatorIndex);
    }
    await props.addReservation(eventInfo.date, resourceId);
    setScrollTime(DateTime.fromJSDate(eventInfo.date).setZone(props.timezone));
  };

  useEffect(() => {
    if (!resourcesWithReservations || !resourcesWithReservations.length) return;

    const events = resourcesWithReservations.map(resource => {
      if (!resource.children || !resource.children.length) return;

      let isInGroup = false;
      if (resource.children.length > 1) {
        isInGroup = true;
      }
      return resource.children.map((child, idx) => {
        return child.map(r => {
          const reservationStart = r.reservationTime.toDate();
          const reservEvent = {
            id: r.id,
            // The lane already says who the appointment is with, so the bar carries the
            // guest and what they booked. Joined with `·` rather than a preposition, for
            // the reason given in plan §16.1 — "with Nathan" but "on Lane 4".
            title: isAppointments && r.serviceName ? `${r.name} · ${r.serviceName}` : r.name,
            resourceId: r.resourceId,
            // Flagged red rather than hidden or blocked — staff create these knowingly
            // (plan §7); the timeline's job is to make it obvious afterwards.
            classNames: overbookedIds?.has(r.id) ? ["fc-event--overbooked"] : [],
            start: reservationStart,
            end:
              r.duration === -1
                ? moment(reservationStart).add(1, "day").toDate()
                : moment(reservationStart).add(r.duration, "minutes").toDate(),
            allDay: r.duration === -1
          };

          if (isInGroup) {
            reservEvent.resourceId = `${reservEvent.resourceId}_${idx + 1}`;
          }

          return reservEvent;
        });
      });
    });

    setTimelineEvents(events.flat(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourcesWithReservations, overbookedIds]);

  // How many lanes to render for a resource. Restaurants keep using the configured
  // capacity. Appointments take it from the packed data instead, because
  // `maxReservations` is meaningless for them (plan §0) and staff may double-book
  // (§7) — the lane count has to follow what the day actually holds, or the extra
  // booking has no row to sit in and vanishes from this view while still showing in
  // the list.
  const laneCount = resource => {
    if (!isAppointments) {
      return resource.maxReservations;
    }
    return resourcesWithReservations?.find(r => r.id === resource.id)?.children?.length || 1;
  };

  useEffect(() => {
    const resources = filter?.resources?.map((r, idx) => {
      const resource = {
        id: r.id,
        title: r.name,
        order: idx
      };
      let index = 0;
      const lanes = laneCount(r);
      if (lanes && lanes > 1) {
        resource.children = Array(lanes)
          .fill(null)
          .map((_, idx) => ({
            id: `${r.id}_${++index}`,
            title: `${r.name} ${idx + 1}`,
            order: idx + 1
          }));
      }

      return resource;
    });
    setTimelineResources(resources);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.resources, resourcesWithReservations, isAppointments]);

  useEffect(() => {
    if (scrollTime) {
      const calendarApi = timelineRef.current.getApi();
      calendarApi?.scrollToTime(scrollTime.toFormat("HH:mm:ss"));
    }
  }, [scrollTime]);

  useEffect(() => {
    if (date && timelineRef.current && moment(date).isValid()) {
      const calendarApi = timelineRef.current.getApi();
      calendarApi?.gotoDate(moment(date).format("YYYY-MM-DD"));
    }
  }, [date]);

  useEffect(() => {
    if (!props.resources || !props.resources.length) setShowNoResourcesModal(true);
  }, [props.resources]);

  return (
    <div className="reservation-table-content">
      <div className="reservations-schedule">
        {showNoResourcesModal && (
          <SweetAlert
            showCancel
            title={"Warning"}
            cancelBtnText="Cancel"
            confirmBtnText="Show me"
            onCancel={() => setShowNoResourcesModal(false)}
            onConfirm={() => props.history.push("/admin/settings?page=reservations")}
          >
            Timeline view requires Bookable resources to be configured.
          </SweetAlert>
        )}
        <div className="hour-reservations fixed-height">
          <FullCalendar
            resourceOrder={"order"}
            timeZone={props.timezone}
            schedulerLicenseKey={licenseKey}
            plugins={[momentPlugin, resourceTimelinePlugin, interactionPlugin]}
            initialView="resourceTimeline"
            resourceAreaHeaderContent={resourceLabel?.plural || "Resources"}
            nowIndicator={true}
            ref={timelineRef}
            now={new Date()}
            slotDuration={"00:15:00"}
            // Undefined rather than a number for restaurants, so the option is absent
            // and FullCalendar's own auto-sizing runs exactly as it does today.
            slotMinWidth={isAppointments ? APPOINTMENT_SLOT_MIN_WIDTH : undefined}
            events={timelineEvents}
            headerToolbar={false}
            eventClick={handleTimelineEventClick}
            dateClick={handleTimelineDateClick}
            resources={timelineResources}
            initialDate={moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : new Date()}
            scrollTimeReset={false}
            scrollTime={null}
          />
        </div>
      </div>
    </div>
  );
}

export default DailyReservationsTimeline;
